import React, { FC } from "react";
import classnames from "classnames";
import { getFullName } from "@shared/utils";
import { LeaderboardCrown } from "@shared/models";

// Hoisted to its own module on this shelf (923-core-context context/engineering/spark-rewards/phase-51-leaderboard-to-design.md's own L5) once the
// per-row value chip (RankRow.tsx) became a second consumer -- see that module's own JSDoc.
import { crownBadgeLetter } from "../crownBadgeLetter";
import "./index.scss";

interface LeaderboardCrownCardProps {
  crown: LeaderboardCrown;
}

/**
 * D4 (still holds): a new, small, single-purpose row — NOT a repurposed `CoreValueCard` (its props
 * require a mandatory `onClickHandler` and it renders the VIEWER's own progress toward a value, a
 * fundamentally different subject from "who's #1 company-wide", with no click/filter interaction in
 * this phase's scope).
 *
 * Design-fix plan (round 2): the Figma "Value crowns" rail is a compact LIST inside one white card,
 * not a grid of gem tiles — this row is a small rounded-square letter badge (`crownBadgeLetter`), the
 * value's own title (tiny uppercase label, never shortened — rendered verbatim, uppercased by CSS) over
 * the winning user's name, and the score at the right. The remote gem artwork (`coreValue.icon_url`)
 * the PREVIOUS tile design rendered via a raw `backgroundImage` div is gone from this row entirely —
 * the field still exists on the wire model but this component no longer reads it.
 *
 * A `null` `user` (no qualifying recognitions for this value in the window) still renders the row's
 * own value title and badge, but replaces the name with a muted "—" and suppresses the score (there is
 * nothing to score) rather than crashing or leaving a blank slot. `leaderboard-crown-card--empty` still
 * greys out the WHOLE row via `filter: grayscale(1); opacity: 0.5` (same "not yet earned" intent
 * `BadgeCard--locked` uses, though not the same mechanism — see this component's own index.scss) —
 * carried over unchanged from the previous tile design, only retargeted at the new row shape, so an
 * unclaimed crown still reads as deliberately empty, not a rendering glitch.
 */
const LeaderboardCrownCard: FC<LeaderboardCrownCardProps> = (props) => {
  const { crown } = props;
  const { coreValue, user, total_score } = crown;

  return (
    <div className={classnames("leaderboard-crown-card", { "leaderboard-crown-card--empty": !user })}>
      <div className="leaderboard-crown-card-badge">{crownBadgeLetter(coreValue.title)}</div>
      <div className="leaderboard-crown-card-body">
        <div className="leaderboard-crown-card-top">
          <div className="leaderboard-crown-card-title">{coreValue.title}</div>
          {user ? <div className="leaderboard-crown-card-score">{total_score}</div> : null}
        </div>
        {user ? (
          <div className="leaderboard-crown-card-user">{getFullName(user)}</div>
        ) : (
          <div
            className="leaderboard-crown-card-user leaderboard-crown-card-user--empty"
            data-testid="crown-card-empty"
          >
            —
          </div>
        )}
      </div>
    </div>
  );
};

export default LeaderboardCrownCard;
